import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  HostListener,
  InjectionToken,
  OnInit,
  Output,
  WritableSignal,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'

import { Observable, combineLatest } from 'rxjs'
import { filter, map, tap } from 'rxjs/operators'

import { FlxThemeModule } from '@flowx/angular-theme'
import {
  FLX_MODAL_DATA,
  FlxButtonModule,
  FlxCardModule,
  FlxIconModule,
  FlxInputModule,
  FlxModalModule,
  FlxModalService,
  FlxSelectModule,
  FlxSpinnerComponent,
} from '@flowx/angular-ui-toolkit'
import {
  CUSTOM_LAST_MODIFIED_FORMAT,
  STATUS_BACKGROUND_COLOR,
  StatusEnum,
  TASK_ACTION_LABELS,
  TaskActions,
  TaskComment,
  TaskHistory,
  TaskHistoryEvent,
  ViewTask,
  getEventDescriptionHtml,
  getOverrideDateFormatter,
  localizeString,
  transformUsersToOptions,
} from '@flowx/core-sdk'

import { FlxLocalizePipe } from '../../../pipes/flx-localize.pipe'
import { FlxTimeElapsedPipe } from '../../../pipes/flx-time-elapsed.pipe'
import { ViewTasksStateService } from '../../services/view-tasks-state.service'

@Component({
  selector: 'flx-task-details',
  imports: [
    CommonModule,
    FlxModalModule,
    FlxCardModule,
    FlxButtonModule,
    FlxIconModule,
    FlxInputModule,
    FlxSelectModule,
    FormsModule,
    ReactiveFormsModule,
    FlxSpinnerComponent,
    FlxTimeElapsedPipe,
    FlxThemeModule,
    FlxLocalizePipe,
  ],
  providers: [ViewTasksStateService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './flx-task-details.component.html',
  styleUrl: './flx-task-details.component.scss',
})
export class FlxTaskDetailsComponent implements OnInit {
  #data: {
    task: ViewTask
    taskService: ViewTasksStateService
    priorityUpdateEmitter: EventEmitter<string>
  } = inject(
    FLX_MODAL_DATA as InjectionToken<{
      task: ViewTask
      taskService: ViewTasksStateService
      priorityUpdateEmitter: EventEmitter<string>
    }>
  )
  #flxModalService: FlxModalService = inject(FlxModalService)
  #destroyRef = inject(DestroyRef)
  #sanitizer = inject(DomSanitizer)

  @Output() dataEventEmitter = new EventEmitter<any>()
  updatedPriority: number
  possibleActions = TaskActions

  selectedTask$: Observable<ViewTask>

  priority = new FormControl('')
  message = new FormControl('', [Validators.required, Validators.minLength(1)])

  taskActionLabels = TASK_ACTION_LABELS

  statusLabels = StatusEnum.reduce(
    (acc, item) => ({
      ...acc,
      [item.id]: localizeString(item.label),
    }),
    {}
  )

  taskContext$: Observable<{
    comments: TaskComment[]
    history: TaskHistory[]
    loading: boolean
  }> = combineLatest([
    this.#data.taskService.taskComments,
    this.#data.taskService.taskHistory,
    this.#data.taskService.taskCommentsLoading,
    this.#data.taskService.taskHistoryLoading,
  ]).pipe(
    map(([comments, history, commentLoading, historyLoading]) => ({
      comments,
      history,
      loading: commentLoading || historyLoading,
    }))
  )

  /* ASSIGNEES*/
  assigneesOptions$: Observable<
    {
      label: string
      value: string
    }[]
  > = this.#data.taskService.assignees.pipe(map((assignees) => transformUsersToOptions(assignees)))
  assigneesLoading$: Observable<boolean> = this.#data.taskService.assigneeLoading
  assigneesError$: Observable<string> = this.#data.taskService.assigneeError
  assigneesContext$: Observable<{
    assignees: { label: string; value: string; enabled: boolean }[]
    loading: boolean
    error: string
  }> = combineLatest([this.assigneesOptions$, this.assigneesLoading$, this.assigneesError$]).pipe(
    tap((data) => {
      this.assigneesLoading.set(data[1])
    }),
    map(([assignees, loading, error]) => ({
      assignees: assignees.map((assignee) => ({ ...assignee, enabled: true })),
      loading,
      error,
    }))
  )
  showAssigneeForm: boolean
  username = new FormControl(null, [Validators.required])
  assigneesCalled = signal(false)
  assigneesLoading = signal(false)
  selectAssigneeEmptyMessage = computed(() =>
    this.assigneesCalled()
      ? this.assigneesLoading()
        ? localizeString('@@sys_tm_loading')
        : localizeString('@@sys_tm_nothing_found')
      : localizeString('@@sys_tm_search_term_validation')
  )
  assignee: WritableSignal<{ value: string; label: string; enabled: boolean }> = signal(null)
  editPriorityMode = signal<boolean>(false)
  canEditPriority = signal<boolean>(true)
  getStatusBackgroundColor = STATUS_BACKGROUND_COLOR

  get disableUpdatePriority(): boolean {
    return this.priority.value === '' || this.priority.value === null
  }

  @HostListener('document:keydown.escape')
  handleEscapePress(): void {
    this.hideTaskModal()
  }

  constructor() {
    effect(() => {
      const isEditing = this.editPriorityMode()
      const value = this.priority.value?.toString().replace(/^-+/, '')

      if (isEditing && value !== this.priority.value?.toString()) {
        this.priority.patchValue(value ?? '')
      }
    })
  }

  preventNegativeInput(event: KeyboardEvent | ClipboardEvent): void {
    if (event instanceof KeyboardEvent) {
      if (event.key === '-' || event.key === 'Minus') {
        event.preventDefault()
      }
    }
    if (event instanceof ClipboardEvent) {
      const pastedInput: string = event.clipboardData?.getData('text') || ''
      if (pastedInput.trim().startsWith('-')) {
        event.preventDefault()
      }
    }
  }

  ngOnInit(): void {
    this.#data.taskService.getTaskComments(this.#data.task.id)
    this.#data.taskService.getTaskHistory(this.#data.task.id)

    this.selectedTask$ = this.#data.taskService.selectedTask.pipe(
      filter((value) => !!value),
      map((_task) => this.mapTaskForModal(_task)),
      tap((_task) => {
        if (_task) {
          if (_task.priority !== null) {
            this.priority.patchValue(_task.priority.toString())
          }
          this.showAssigneeForm = Boolean(
            _task.taskDetailsActions[this.possibleActions.ASSIGN] ||
            (!_task.taskDetailsActions[this.possibleActions.ASSIGN] && _task.currentSwimlane?.owner)
          )
          if (_task.currentSwimlane?.owner?.username) {
            const value = {
              value: _task.currentSwimlane?.owner?.username,
              label: `${_task.currentSwimlane?.owner?.firstName || '-'} ${_task.currentSwimlane?.owner?.lastName || '-'} (${
                _task.currentSwimlane?.owner?.username
              })`,
              enabled: _task.currentSwimlane?.owner?.enabled ?? true,
            }
            // task swimlane owner enabled is not relevant so we need this
            this.#data.taskService.resetAssignees()
            this.#data.taskService
              .getAssignees(_task, { keyword: value.value })
              .pipe(takeUntilDestroyed(this.#destroyRef))
              .subscribe(() => {
                this.assignee.set(value)
                this.username.patchValue(value)
              })
          } else {
            this.username.reset(null, { onlySelf: true, emitEvent: false })
            this.assignee.set(null)
            this.#data.taskService.resetAssignees()
          }
          if (_task.taskDetailsActions[this.possibleActions.ASSIGN]) {
            this.username.enable()
          } else {
            this.username.disable()
          }

          if (!_task.taskDetailsActions[this.possibleActions.CHANGE_PRIORITY]) {
            this.canEditPriority.set(false)
          }
        }
      })
    )
  }

  hideTaskModal(): void {
    this.dataEventEmitter.emit({ updatedPriority: this.updatedPriority })
    this.#flxModalService.hide()
    this.#data.taskService.resetTaskComments()
    this.#data.taskService.resetTaskHistory()
    this.#data.taskService.resetAssignees()
  }

  mapTaskForModal(task: ViewTask): ViewTask {
    return {
      ...task,
      taskDetailsActions: this.#data.taskService.buildTaskActions(task),
    }
  }

  getAssignees(searchTerm: string, task: ViewTask): void {
    this.assigneesCalled.set(searchTerm?.length >= 4)
    if (searchTerm?.length >= 4) {
      this.#data.taskService
        .getAssignees(task, { keyword: searchTerm })
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe()
    } else {
      this.#data.taskService.resetAssignees()
    }
  }

  addComment(task: ViewTask): void {
    if (!this.message.value) {
      return
    }

    this.#data.taskService.addComment(task.id, { message: this.message.value }).subscribe(() => {
      this.message.reset()
    })
  }

  deleteComment(comment: TaskComment, task: ViewTask): void {
    this.#data.taskService.deleteComment(task.id, comment.commentId).subscribe()
  }

  clearAndAssignUser(task: ViewTask): void {
    if (this.username.valid && this.username.value.value !== this.assignee()?.value) {
      task.taskDetailsActions[this.possibleActions.ASSIGN].onClick(this.username.value.value)
      this.assignee.set(this.username.value)
      this.#data.taskService.resetAssignees()
    }
  }

  cancelEditPriority(selectedTask: ViewTask): void {
    const value = selectedTask.priority ? selectedTask.priority.toString() : ''
    this.priority.patchValue(value)
    this.editPriorityMode.set(false)
  }

  updatePriority(selectedTask: ViewTask): void {
    const parsedValue = parseInt(this.priority.value, 10)
    const newValue = isNaN(parsedValue) ? null : parsedValue

    if (selectedTask.priority === newValue) {
      this.editPriorityMode.set(false)
      return
    }

    this.#data.taskService.updatePriority(selectedTask, newValue).subscribe({
      next: () => {
        selectedTask.priority = newValue
        this.#data.taskService.analytics?.priorityChanged(newValue)
        this.editPriorityMode.set(false)
        return this.#data.priorityUpdateEmitter.emit('success')
      },
      error: (error) => this.#data.priorityUpdateEmitter.emit(error?.message),
    })
  }

  formatDate(value: string): string {
    return getOverrideDateFormatter(CUSTOM_LAST_MODIFIED_FORMAT)?.format(new Date(value)) || value
  }

  getEventDescriptionHtml(event: TaskHistoryEvent): SafeHtml {
    return this.#sanitizer.bypassSecurityTrustHtml(getEventDescriptionHtml(event))
  }
}
